import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Building2,
  Compass,
  Cpu,
  Handshake,
  MapPin,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";
import { Button } from "../components/ui/button";

const fade = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const heroProof = [
  { value: "150+", label: "Years combined operator experience" },
  { value: "15", label: "Hospitals served" },
  { value: "250+", label: "Clinics served" },
  { value: "100%", label: "Healthcare IT — our only business" },
];

const snapshot = [
  {
    label: "Headquarters",
    value: "Southern California",
    detail: "Delivery teams placed nationwide for round-the-clock coverage.",
  },
  {
    label: "Focus",
    value: "Healthcare IT — exclusively",
    detail: "Every hour of engineering, research, and partnership goes here.",
  },
  {
    label: "Footprint",
    value: "15 hospitals · 250+ clinics · 6 states",
    detail: "Serving 15M+ patients across acute, ambulatory, and post-acute.",
  },
  {
    label: "Led by",
    value: "Former CIOs, CISOs, and CTOs",
    detail: "Operators who ran the job before building this firm.",
  },
];

const benchAggregate = [
  { value: "150+", label: "Years combined operator experience" },
  { value: "300+", label: "Healthcare programs shipped" },
  { value: "100%", label: "Operator-led engagements" },
];

const benchPillars = [
  {
    icon: Building2,
    role: "The CIO bench",
    ran: "Multi-hospital systems, academic medical centers, integrated delivery networks.",
    hallmark:
      "Board-level program ownership across EHR, infrastructure, analytics, and M&A integration.",
  },
  {
    icon: ShieldCheck,
    role: "The CISO bench",
    ran: "Clinical and administrative security programs, HIPAA and HITRUST postures, incident response.",
    hallmark:
      "Ransomware recovery, zero-trust architecture, and regulator-ready governance under pressure.",
  },
  {
    icon: Cpu,
    role: "The CTO bench",
    ran: "Healthcare platforms, clinical applications, cloud modernization programs.",
    hallmark:
      "Production engineering discipline — from EHR integrations to AI and analytics deployments.",
  },
  {
    icon: Compass,
    role: "The advisory bench",
    ran: "Turnarounds, M&A IT integrations, vendor rationalization, and operating model redesigns.",
    hallmark:
      "Interim leadership, board reporting, and the uncomfortable conversations that unstick programs.",
  },
];

const commitments = [
  {
    icon: Target,
    title: "Healthcare is our only business",
    detail:
      "We don't consult across verticals or chase new industries. Every hour of our R&D, partnerships, and bench goes to healthcare IT — because the market is too specialized to split attention.",
  },
  {
    icon: Handshake,
    title: "We pick clients the way they pick us",
    detail:
      "We turn down work we can't meaningfully improve. Misaligned goals, underwritten timelines, mandates that won't actually land — we'd rather walk than ship theater. What we take on, we commit to.",
  },
  {
    icon: Sparkles,
    title: "We speak plainly to boards",
    detail:
      "Our operators have sat on the CIO side of a board table. We translate technical risk into governance language, and governance priorities into execution — without buzzwords or dashboards that obscure.",
  },
  {
    icon: ShieldCheck,
    title: "We stay through the hard part",
    detail:
      "Go-live isn't the finish line. We stay through stabilization, transition, and the first real production incident — long enough that the next crisis belongs to your team, and your team wants it that way.",
  },
];

export default function WhoWeAre() {
  const reduceMotion = useReducedMotion();

  return (
    <>
      <Helmet>
        <title>
          Who We Are | Velozent — Operator-Led Healthcare IT Firm
        </title>
        <meta
          name="description"
          content="Velozent is a healthcare IT firm built by former CIOs, CISOs, and CTOs. Headquartered in Southern California, delivering nationwide across 15 hospitals, 250+ clinics, and 15M+ patients."
        />
        <meta
          name="keywords"
          content="healthcare IT firm, operator-led healthcare IT, former CIO CISO CTO, Velozent leadership, Southern California healthcare IT"
        />
        <meta
          property="og:title"
          content="Who We Are | Velozent — Operator-Led Healthcare IT Firm"
        />
        <meta
          property="og:description"
          content="Built by former healthcare IT operators to be the firm they wished they'd had to hire."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/who-we-are" />
        <link rel="canonical" href="https://velozent.com/who-we-are" />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-shark-950 px-4 pb-16 pt-20 text-white lg:pb-20 lg:pt-24">
        <div className="hero-mesh">
          <div className="hero-mesh-orb hero-mesh-orb--1" />
          <div className="hero-mesh-orb hero-mesh-orb--2" />
          <div className="hero-mesh-orb hero-mesh-orb--3" />
          <div className="hero-mesh-grain" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl text-center">
          <motion.div
            variants={reduceMotion ? undefined : stagger}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fade}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400"
            >
              Who we are
            </motion.p>

            <motion.h1
              variants={fade}
              className="mx-auto mt-4 max-w-4xl text-balance text-[clamp(2rem,4.6vw,3.15rem)] font-extrabold leading-[1.08] tracking-tight text-white"
            >
              The healthcare IT firm{" "}
              <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
                built by the operators who ran IT
              </span>
            </motion.h1>

            <motion.p
              variants={fade}
              className="mx-auto mt-5 max-w-2xl text-[1.05rem] leading-relaxed text-white/75"
            >
              Velozent was founded by former CIOs, CISOs, and CTOs who spent
              their careers inside health systems — and built this firm to be
              the partner they wished they'd had to hire.
            </motion.p>

            <motion.div
              variants={fade}
              className="mx-auto mt-10 grid max-w-3xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm sm:grid-cols-4"
            >
              {heroProof.map((p) => (
                <div
                  key={p.label}
                  className="bg-shark-950/80 px-4 py-4 text-center sm:py-5"
                >
                  <p className="text-[1.35rem] font-extrabold tracking-tight text-white sm:text-[1.5rem]">
                    {p.value}
                  </p>
                  <p className="mt-1 text-[10.5px] font-semibold uppercase leading-tight tracking-[0.12em] text-white/55">
                    {p.label}
                  </p>
                </div>
              ))}
            </motion.div>

            <motion.div
              variants={fade}
              className="mt-9 flex flex-wrap items-center justify-center gap-4"
            >
              <Button
                asChild
                size="lg"
                className="group h-12 border-0 px-8 text-[14px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)]"
                style={{ backgroundColor: "#e76d38" }}
              >
                <Link to="/contact">
                  Talk to a Velozent operator
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <a
                href="#origin"
                className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
              >
                Why we started
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            <motion.p
              variants={fade}
              className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40"
            >
              No obligation · No pitch deck · 15-minute call
            </motion.p>
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-burnt-sienna-500/50 to-transparent" />
      </section>

      {/* ── Origin ───────────────────────────────────────────────────────── */}
      <section
        id="origin"
        className="relative bg-white px-4 py-16 lg:px-8 lg:py-20"
      >
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              Why Velozent exists
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              We started this firm because the market kept failing our peers
            </h2>
            <div
              className="mt-6 h-px w-10 bg-complement-500/70"
              aria-hidden
            />
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14"
          >
            {/* Narrative */}
            <motion.div variants={fade} className="space-y-5">
              <p className="text-[0.98rem] leading-[1.75] text-shark-600">
                Before Velozent, our leadership team ran IT across health
                systems, integrated delivery networks, and physician
                organizations. We saw the same pattern in every room we sat in:
                board-level problems being addressed with slide-deck-level
                answers.
              </p>
              <p className="text-[0.98rem] leading-[1.75] text-shark-600">
                The firms our peers hired didn't actually run the job. Big 4
                advisors arrived with analysts who'd never touched a production
                EHR. Traditional MSPs treated healthcare as one workload among
                many. The partner a CIO actually needed — one who'd lived in
                the seat, understood the politics, and could land a change
                without breaking operations — didn't exist at scale.
              </p>
              <p className="text-[0.98rem] leading-[1.75] text-shark-600">
                Velozent is the firm we wished we'd had to hire. Operators on
                the masthead. One industry, not seven. Runbooks instead of
                recommendations. Outcomes committed in writing — not deflected
                into a change-order cycle. A different shape of firm, built
                deliberately.
              </p>

              <div className="mt-6 flex items-start gap-3 rounded-xl border border-shark-200 bg-shark-50/60 p-4">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-complement-600" />
                <p className="text-[0.88rem] leading-relaxed text-shark-600">
                  <span className="font-bold text-shark-900">
                    Headquartered in Southern California.
                  </span>{" "}
                  Delivery resources are placed across the country so clients
                  who need round-the-clock attention always have an operator
                  nearby.
                </p>
              </div>
            </motion.div>

            {/* Snapshot card */}
            <motion.aside
              variants={fade}
              className="relative overflow-hidden rounded-2xl border border-shark-200 bg-gradient-to-br from-shark-50/60 via-white to-white p-6 sm:p-7"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-complement-600">
                Company snapshot
              </p>
              <h3 className="mt-2 text-[1.15rem] font-extrabold text-shark-900">
                Who we are, in plain language
              </h3>

              <dl className="mt-6 divide-y divide-shark-200">
                {snapshot.map((row) => (
                  <div
                    key={row.label}
                    className="grid grid-cols-[110px_1fr] gap-5 py-4 first:pt-0 last:pb-0 sm:grid-cols-[130px_1fr]"
                  >
                    <dt className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                      {row.label}
                    </dt>
                    <dd>
                      <p className="text-[0.95rem] font-bold text-shark-900">
                        {row.value}
                      </p>
                      <p className="mt-1 text-[0.82rem] leading-relaxed text-shark-500">
                        {row.detail}
                      </p>
                    </dd>
                  </div>
                ))}
              </dl>
            </motion.aside>
          </motion.div>
        </div>
      </section>

      {/* ── The bench ────────────────────────────────────────────────────── */}
      <section className="relative bg-shark-50/60 px-4 py-16 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              The bench
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              Senior leaders who've already done the job
            </h2>
            <div
              className="mt-6 h-px w-10 bg-complement-500/70"
              aria-hidden
            />
            <p className="mt-5 max-w-[620px] text-[0.98rem] leading-[1.65] text-shark-500">
              Velozent's leadership comes from inside the systems we now serve
              — CIOs, CISOs, CTOs, and advisory operators who built and ran
              the job before starting this firm. The bench, not the brochure,
              is the product.
            </p>
          </div>

          {/* Aggregate stats */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3"
          >
            {benchAggregate.map((s) => (
              <motion.div
                key={s.label}
                variants={fade}
                className="rounded-xl border border-shark-200 bg-white px-5 py-5"
              >
                <p className="text-[1.75rem] font-extrabold leading-none tracking-tight text-shark-950">
                  {s.value}
                </p>
                <p className="mt-2 text-[11px] font-bold uppercase leading-tight tracking-[0.14em] text-shark-500">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Bench pillars */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2"
          >
            {benchPillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.article
                  key={p.role}
                  variants={fade}
                  className="group relative overflow-hidden rounded-2xl border border-shark-200 bg-white p-6 sm:p-7"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-complement-50 text-complement-700 ring-1 ring-complement-200">
                      <Icon className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <div>
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                        Bench · {String(i + 1).padStart(2, "0")}
                      </p>
                      <h3 className="mt-1 text-[1.05rem] font-extrabold text-shark-900">
                        {p.role}
                      </h3>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3 border-t border-shark-100 pt-4">
                    <div>
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                        Where they've run it
                      </p>
                      <p className="mt-1 text-[0.9rem] leading-relaxed text-shark-600">
                        {p.ran}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                        Hallmark
                      </p>
                      <p className="mt-1 text-[0.9rem] leading-relaxed text-shark-600">
                        {p.hallmark}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── What we stand for ────────────────────────────────────────────── */}
      <section className="relative bg-white px-4 py-16 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-600">
              What we stand for
            </p>
            <h2 className="mt-4 max-w-2xl text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold leading-[1.12] tracking-tight text-shark-950">
              Four commitments that define the firm
            </h2>
            <div
              className="mt-6 h-px w-10 bg-complement-500/70"
              aria-hidden
            />
            <p className="mt-5 max-w-[620px] text-[0.98rem] leading-[1.65] text-shark-500">
              These aren't delivery methods — those live on the Why Velozent
              page. These are the non-negotiables of the firm itself: who we
              are before any engagement begins.
            </p>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2"
          >
            {commitments.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.article
                  key={c.title}
                  variants={fade}
                  className="relative overflow-hidden rounded-2xl border border-shark-200 bg-shark-50/40 p-6 sm:p-7"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-complement-600 ring-1 ring-shark-200">
                      <Icon className="h-5 w-5" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-shark-400">
                        Commitment · {String(i + 1).padStart(2, "0")}
                      </p>
                      <h3 className="mt-1 text-[1.05rem] font-extrabold text-shark-900">
                        {c.title}
                      </h3>
                    </div>
                  </div>
                  <p className="mt-4 text-[0.92rem] leading-[1.65] text-shark-600">
                    {c.detail}
                  </p>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden px-4 py-14 text-center lg:py-20">
        <div
          className="absolute inset-0 bg-gradient-to-br from-shark-950 via-complement-950 to-shark-950"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(231,109,56,0.15),transparent_70%)]"
          aria-hidden
        />

        <div className="relative mx-auto max-w-3xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400">
            Start the conversation
          </p>
          <h2 className="mt-3 text-[clamp(1.625rem,3vw,2.15rem)] font-extrabold leading-[1.15] tracking-tight text-white">
            Meet the operators before you sign anything
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[0.98rem] leading-relaxed text-white/70">
            A 15-minute call with a Velozent operator. We'll tell you where we
            can help, where we can't, and what a first engagement would look
            like in your environment — no pitch deck.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="group h-12 border-0 px-8 text-[14px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)]"
              style={{ backgroundColor: "#e76d38" }}
            >
              <Link to="/contact">
                Book Your 15-Minute Strategy Call
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
            <Link
              to="/why-velozent"
              className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white/65 transition-colors hover:text-white"
            >
              See how we compare
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
            No obligation · No pitch deck · 15-minute call
          </p>
        </div>
      </section>
    </>
  );
}
